import endpoints from '@app/src/api/endpoints'
import { FetchKey } from '@app/src/api/fetchHooks'
import Permissions, { usePermissions } from '@app/src/auth/permissions'
import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import DrawerViewFilters from '@app/src/components/Drawer/Views/DrawerViewFilters'
import { OptionIcon } from '@app/src/components/Form/Select'
import { useAutocompleteStyles } from '@app/src/components/Form/Select/SimpleSelect'
import { FinalRiskRatingIcon } from '@app/src/components/Table/Cells/FinalRiskRatingCell'
import { useAuthentication } from '@app/src/context/AuthenticationContext'
import CustomCategoriesFilters from '@app/src/pages/ResourceCollection/Filters/CustomCategoriesFilters'
import Filter from '@app/src/pages/ResourceCollection/Filters/Filter'
import FilterFacetSelect from '@app/src/pages/ResourceCollection/Filters/FilterFacetSelect'
import type { FiltersContextProps } from '@app/src/pages/ResourceCollection/Filters/Filters'
import Filters from '@app/src/pages/ResourceCollection/Filters/Filters'
import FilterSection from '@app/src/pages/ResourceCollection/Filters/FilterSection'
import { PeriodFilterOptions } from '@app/src/pages/ResourceCollection/Filters/PeriodFilterDisplay'
import type { FilterGroup } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { Operators, RESPONSE_LATEST_SUBMITTED_FILTER } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type { ResetPage } from '@app/src/types/filter'
import { SortOrder } from '@app/src/types/filter'
import { RequestTargetType } from '@app/src/types/resourceExplorer'
import { Solutions } from '@app/src/wf-constants'
import { Box, Button, Chip, createFilterOptions, Grid, Tooltip } from '@mui/material'
import { useFlags } from 'launchdarkly-react-client-sdk'
import React from 'react'
import { useIntl } from 'react-intl'
import type { FacetItem } from '..'
import AllFiltersButton from './AllFiltersButton'
import AssessmentTemplateSelectorWithLevelFilter from './AssessmentMiniFilter/AssessmentTemplateSelectorWithLevelFilter'
import useLocalisedCountryFilter from './Common/useLocalisedCountryFilter'
import FilterSelect from './FilterSelect'
import OptionWithLabel from './OptionWithLabel'
import ProductMappingFilters from './ProductMappingFilters'
import RangeFilter from './RangeFilter'
import SpendFilter from './SpendFilter'
import StatusOption from './StatusOption'

interface ResponseFiltersProps {
  implicitFilters?: FilterGroup[]
  allowedFilters: FiltersContextProps['allowedFilters']
  resetPage?: ResetPage
}

const ResponsesFilters = ({
  allowedFilters,
  resetPage,
  implicitFilters: baseImplicitFilters = [],
}: ResponseFiltersProps): JSX.Element => {
  const { renderWithPermission } = usePermissions()
  const autocompleteClasses = useAutocompleteStyles()
  const { formatMessage } = useIntl()
  const { hasPermission } = usePermissions()
  const verificationEnabled = hasPermission(Permissions.WRITE_VERIFICATION)
  const { solution } = useAuthentication().scope
  const { openDrawer } = useDrawer()
  const canAccessAssessmentTemplate = hasPermission(Permissions.ASSESSMENT_TEMPLATE_ACCESS)
  const implicitFilters = [RESPONSE_LATEST_SUBMITTED_FILTER, ...baseImplicitFilters]
  const { renderOption, renderTags, customFilterOption, customSort } = useLocalisedCountryFilter()
  const { productMapping } = useFlags()

  const isProviderSpecific = implicitFilters.some(filter => filter.name === 'request.subscriptions.targetProviderId')
  const md = isProviderSpecific ? 5 : 3
  const md2 = isProviderSpecific ? 2.5 : 2

  const tierFilter = createFilterOptions<FacetItem>({
    stringify: option =>
      formatMessage(
        {
          id: `schemas.provider.tierValues`,
        },
        { tier: option.value },
      ),
  })

  return (
    <Grid container spacing={1}>
      <Filters allowedFilters={allowedFilters} resetPage={resetPage}>
        {({ filters, clearQueryFilter, inDrawerFiltersCount }) => {
          const isFiltered = Object.keys(filters).length > 0
          return (
            <>
              {!isProviderSpecific && (
                <Grid item md={md} sm={10}>
                  <Filter name="request.subscriptions.target.id" operator={Operators.In} isQuickFilter>
                    {({ value, onChange, filterName }) => (
                      <FilterFacetSelect
                        implicitFilters={implicitFilters}
                        size="small"
                        multiple
                        facetsParam={{
                          key: [FetchKey.ResponseFacets, filterName],
                          endpoint: endpoints.responsesWithFacets,
                          facetsParam: [{ name: 'request.subscriptions.target.name' }],
                          sort: { target: 'label', order: SortOrder.ASCENDING },
                        }}
                        filterName={filterName}
                        onChange={value => onChange(value)}
                        fieldLabel={formatMessage({ id: 'schemas.filter.companyName' })}
                        value={[value].flat()}
                      />
                    )}
                  </Filter>
                </Grid>
              )}
              <Grid item md={md} sm={10}>
                <Filter name="request.template.id" operator={Operators.In} isQuickFilter>
                  {({ value, onChange, filterName }) => (
                    <FilterFacetSelect
                      implicitFilters={implicitFilters}
                      size="small"
                      multiple
                      facetsParam={{
                        key: [FetchKey.ResponseFacets, filterName],
                        endpoint: endpoints.responsesWithFacets,
                        facetsParam: [{ name: 'request.template.title' }],
                        sort: { target: 'label', order: SortOrder.ASCENDING },
                      }}
                      filterName={filterName}
                      onChange={onChange}
                      fieldLabel={formatMessage({ id: 'schemas.filter.requestName' })}
                      value={[value].flat()}
                    />
                  )}
                </Filter>
              </Grid>
              <Grid item md={md2}>
                <Filter name="request.periodName" operator={Operators.In} isQuickFilter>
                  {({ value, onChange, filterName }) => (
                    <FilterFacetSelect
                      implicitFilters={implicitFilters}
                      multiple
                      facetsParam={{
                        key: [FetchKey.ResponseFacets, filterName],
                        endpoint: endpoints.responsesWithFacets,
                        facetsParam: [{ name: filterName, isEnum: true }],
                        sort: { target: 'label', order: SortOrder.DESCENDING },
                      }}
                      filterName={filterName}
                      onChange={value => onChange(value)}
                      fieldLabel={formatMessage({ id: 'schemas.request.period' })}
                      value={[value].flat()}
                      {...PeriodFilterOptions}
                    />
                  )}
                </Filter>
              </Grid>
              <Grid item md={md2}>
                <Filter name="request.requestStatus" operator={Operators.In} isQuickFilter>
                  {({ value, onChange, filterName }) => (
                    <FilterFacetSelect
                      implicitFilters={implicitFilters}
                      size="small"
                      multiple
                      facetsParam={{
                        key: [FetchKey.ResponseFacets, filterName],
                        endpoint: endpoints.responsesWithFacets,
                        facetsParam: [{ name: filterName, isEnum: true }],
                      }}
                      renderOption={(props, option, { selected }): JSX.Element => (
                        <StatusOption
                          key={option.label}
                          props={props}
                          option={option}
                          selected={selected}
                          verification
                        />
                      )}
                      filterName={filterName}
                      onChange={onChange}
                      fieldLabel={formatMessage({ id: 'schemas.filter.IncludeRequestStatus' })}
                      value={[value].flat()}
                      skipNotSet={false}
                      renderTags={(value, getTagProps) =>
                        value?.map(
                          (option, index) =>
                            option && (
                              <Tooltip
                                key={option?.value?.toString() || index}
                                title={formatMessage({
                                  id: `reporting.statuses.${option.label}`,
                                })}
                                classes={{ tooltipPlacementBottom: autocompleteClasses.tooltip }}
                                arrow
                              >
                                <Chip
                                  label={formatMessage({
                                    id: `reporting.statuses.${option.label}`,
                                  })}
                                  size="small"
                                  {...getTagProps({ index })}
                                />
                              </Tooltip>
                            ),
                        )
                      }
                    />
                  )}
                </Filter>
              </Grid>
              <Grid item md={2}>
                <Box display="inline-block">
                  <AllFiltersButton
                    inDrawerFiltersCount={inDrawerFiltersCount}
                    openDrawer={() =>
                      openDrawer(
                        <DrawerViewFilters allowedFilters={allowedFilters}>
                          <Box px={2} mt={2}>
                            <Filter name="request.periodName" operator={Operators.In}>
                              {({ value, onChange, filterName }) => (
                                <FilterFacetSelect
                                  implicitFilters={implicitFilters}
                                  size="small"
                                  multiple
                                  facetsParam={{
                                    key: [FetchKey.ResponseFacets, filterName],
                                    endpoint: endpoints.responsesWithFacets,
                                    facetsParam: [{ name: filterName, isEnum: true }],
                                    sort: { target: 'label', order: SortOrder.DESCENDING },
                                  }}
                                  filterName={filterName}
                                  onChange={value => onChange(value)}
                                  fieldLabel={formatMessage({ id: 'schemas.request.period' })}
                                  value={[value].flat()}
                                  {...PeriodFilterOptions}
                                />
                              )}
                            </Filter>
                            <FilterSection label={formatMessage({ id: 'schemas.filter.responses' })}>
                              <Filter name="request.template.id" operator={Operators.In}>
                                {({ value, onChange, filterName }) => (
                                  <FilterFacetSelect
                                    implicitFilters={implicitFilters}
                                    size="small"
                                    multiple
                                    facetsParam={{
                                      key: [FetchKey.ResponseFacets, filterName],
                                      endpoint: endpoints.responsesWithFacets,
                                      facetsParam: [{ name: 'request.template.title' }],
                                      sort: { target: 'label', order: SortOrder.ASCENDING },
                                    }}
                                    filterName={filterName}
                                    onChange={onChange}
                                    fieldLabel={formatMessage({ id: 'schemas.filter.requestName' })}
                                    value={[value].flat()}
                                  />
                                )}
                              </Filter>
                              {productMapping && (
                                <>
                                  <Filter name="request.subscriptions.targetType" operator={Operators.EqualTo}>
                                    {({ value, onChange }) => (
                                      <FilterSelect
                                        size="small"
                                        multiple={false}
                                        options={[
                                          {
                                            value: RequestTargetType.Product,
                                            label: formatMessage({ id: 'schemas.mappingNode.product' }),
                                          },
                                          {
                                            value: RequestTargetType.Provider,
                                            label: formatMessage({ id: 'resourceCollections.general.company' }),
                                          },
                                        ]}
                                        onChange={value => onChange(value)}
                                        fieldLabel={formatMessage({ id: 'reporting.requestedFor' })}
                                        value={value as string}
                                      />
                                    )}
                                  </Filter>
                                  <Filter name="request.subscriptions.targetProductId" operator={Operators.In}>
                                    {({ value, onChange, filterName }) => (
                                      <FilterFacetSelect
                                        implicitFilters={implicitFilters}
                                        size="small"
                                        multiple
                                        facetsParam={{
                                          key: [FetchKey.ResponseFacets, filterName],
                                          endpoint: endpoints.responsesWithFacets,
                                          facetsParam: [{ name: 'request.subscriptions.targetProduct.name' }],
                                        }}
                                        filterName={filterName}
                                        onChange={onChange}
                                        fieldLabel={formatMessage({ id: 'schemas.mappingNode.product' })}
                                        value={[value].flat()}
                                      />
                                    )}
                                  </Filter>
                                </>
                              )}

                              <Filter name="request.requestStatus" operator={Operators.In}>
                                {({ value, onChange, filterName }) => (
                                  <FilterFacetSelect
                                    implicitFilters={implicitFilters}
                                    size="small"
                                    multiple
                                    facetsParam={{
                                      key: [FetchKey.ResponseFacets, filterName],
                                      endpoint: endpoints.responsesWithFacets,
                                      facetsParam: [{ name: filterName, isEnum: true }],
                                    }}
                                    renderOption={(props, option, { selected }): JSX.Element => (
                                      <StatusOption
                                        key={option.label}
                                        props={props}
                                        option={option}
                                        selected={selected}
                                        verification
                                      />
                                    )}
                                    renderTags={(value, getTagProps) =>
                                      value?.map(
                                        (option, index) =>
                                          option && (
                                            <Tooltip
                                              key={option?.value?.toString() || index}
                                              title={formatMessage({
                                                id: `reporting.statuses.${option.label}`,
                                              })}
                                              classes={{ tooltipPlacementBottom: autocompleteClasses.tooltip }}
                                              arrow
                                            >
                                              <Chip
                                                label={formatMessage({
                                                  id: `reporting.statuses.${option.label}`,
                                                })}
                                                size="small"
                                                {...getTagProps({ index })}
                                              />
                                            </Tooltip>
                                          ),
                                      )
                                    }
                                    skipNotSet={false}
                                    filterName={filterName}
                                    onChange={value => onChange(value)}
                                    fieldLabel={formatMessage({ id: 'schemas.filter.IncludeRequestStatus' })}
                                    value={[value].flat()}
                                  />
                                )}
                              </Filter>
                              <Filter name="hasExpiredFiles" operator={Operators.In}>
                                {({ value, onChange, filterName }) => (
                                  <FilterFacetSelect
                                    implicitFilters={implicitFilters}
                                    size="small"
                                    multiple
                                    facetsParam={{
                                      key: [FetchKey.ResponseFacets, filterName],
                                      endpoint: endpoints.responsesWithFacets,
                                      facetsParam: [{ name: 'hasExpiredFiles', isEnum: true }],
                                    }}
                                    renderOption={(props, option, { selected }) => (
                                      <OptionWithLabel
                                        key={option.label}
                                        props={props}
                                        label={
                                          option.value === true
                                            ? formatMessage({ id: 'filter.true' })
                                            : option.value === false
                                              ? formatMessage({ id: 'filter.false' })
                                              : String(option.label)
                                        }
                                        multiple
                                        selected={selected}
                                      />
                                    )}
                                    renderTags={(value, getTagProps) =>
                                      value?.map(
                                        (option, index) =>
                                          option && (
                                            <Chip
                                              {...getTagProps({ index })}
                                              key={option?.value?.toString() || index}
                                              label={
                                                option.value === true
                                                  ? formatMessage({ id: 'filter.true' })
                                                  : option.value === false
                                                    ? formatMessage({ id: 'filter.false' })
                                                    : String(option.label)
                                              }
                                              size="small"
                                            />
                                          ),
                                      )
                                    }
                                    filterName={filterName}
                                    onChange={onChange}
                                    fieldLabel={formatMessage({ id: 'schemas.filter.expiredFiles' })}
                                    value={[value].flat()}
                                  />
                                )}
                              </Filter>
                            </FilterSection>
                            {!isProviderSpecific && (
                              <FilterSection label={formatMessage({ id: 'schemas.filter.companyDetails' })}>
                                <Filter name="request.subscriptions.target.id" operator={Operators.In}>
                                  {({ value, onChange, filterName }) => (
                                    <FilterFacetSelect
                                      implicitFilters={implicitFilters}
                                      size="small"
                                      multiple
                                      facetsParam={{
                                        key: [FetchKey.ResponseFacets, filterName],
                                        endpoint: endpoints.responsesWithFacets,
                                        facetsParam: [{ name: 'request.subscriptions.target.id' }],
                                        sort: {
                                          order: SortOrder.ASCENDING,
                                          target: 'label',
                                        },
                                      }}
                                      filterName={filterName}
                                      onChange={value => onChange(value)}
                                      fieldLabel={formatMessage({ id: 'schemas.filter.companyId' })}
                                      value={[value].flat()}
                                    />
                                  )}
                                </Filter>
                                <Filter name="request.subscriptions.target.countryId" operator={Operators.In}>
                                  {({ value, onChange, filterName }) => (
                                    <FilterFacetSelect
                                      implicitFilters={implicitFilters}
                                      size="small"
                                      multiple
                                      facetsParam={{
                                        key: [FetchKey.ResponseFacets, filterName],
                                        endpoint: endpoints.responsesWithFacets,
                                        facetsParam: [{ name: 'request.subscriptions.target.country.iso3166Alpha2' }],
                                      }}
                                      renderOption={renderOption}
                                      renderTags={renderTags}
                                      filterName={filterName}
                                      filterOptions={customFilterOption}
                                      customSort={customSort}
                                      onChange={value => onChange(value)}
                                      fieldLabel={formatMessage({ id: 'schemas.filter.country.countryCode' })}
                                      value={[value].flat()}
                                    />
                                  )}
                                </Filter>
                                <Filter name="request.subscriptions.target.id" operator={Operators.In}>
                                  {({ value, onChange, filterName }) => (
                                    <FilterFacetSelect
                                      implicitFilters={implicitFilters}
                                      size="small"
                                      multiple
                                      facetsParam={{
                                        key: [FetchKey.ResponseFacets, filterName],
                                        endpoint: endpoints.responsesWithFacets,
                                        facetsParam: [{ name: 'request.subscriptions.target.name' }],
                                        sort: { target: 'label', order: SortOrder.ASCENDING },
                                      }}
                                      filterName={filterName}
                                      onChange={value => onChange(value)}
                                      fieldLabel={formatMessage({ id: 'schemas.filter.companyName' })}
                                      value={[value].flat()}
                                    />
                                  )}
                                </Filter>
                              </FilterSection>
                            )}
                            <FilterSection label={formatMessage({ id: 'schemas.filter.responseDetails' })}>
                              {verificationEnabled && (
                                <>
                                  <Filter name="verifications.internalVerificationStatus" operator={Operators.In}>
                                    {({ value, onChange, filterName }) => (
                                      <FilterFacetSelect
                                        implicitFilters={implicitFilters}
                                        size="small"
                                        multiple
                                        facetsParam={{
                                          key: [FetchKey.ResponseFacets, filterName],
                                          endpoint: endpoints.responsesWithFacets,
                                          facetsParam: [{ name: filterName }],
                                        }}
                                        renderOption={(props, option, { selected }): JSX.Element => (
                                          <StatusOption
                                            key={option.label}
                                            props={props}
                                            option={option}
                                            selected={selected}
                                            verification
                                          />
                                        )}
                                        filterName={filterName}
                                        onChange={value => onChange(value)}
                                        fieldLabel={formatMessage({
                                          id: 'filters.responseItem.internalVerificationStatus',
                                        })}
                                        value={[value].flat()}
                                        renderTags={(value, getTagProps) =>
                                          value?.map(
                                            (option, index) =>
                                              option && (
                                                <Tooltip
                                                  key={option?.value?.toString() || index}
                                                  title={formatMessage({
                                                    id: `reporting.internalVerificationStatuses.${option.label}`,
                                                  })}
                                                  classes={{ tooltipPlacementBottom: autocompleteClasses.tooltip }}
                                                  arrow
                                                >
                                                  <Chip
                                                    label={formatMessage({
                                                      id: `reporting.internalVerificationStatuses.${option.label}`,
                                                    })}
                                                    size="small"
                                                    {...getTagProps({ index })}
                                                  />
                                                </Tooltip>
                                              ),
                                          )
                                        }
                                        skipNotSet={false}
                                      />
                                    )}
                                  </Filter>
                                </>
                              )}
                              <RangeFilter
                                uniqueIdFrom="responseDateFrom"
                                uniqueIdTo="responseDateTo"
                                name="submittedAt"
                                fieldLabelFrom={formatMessage({ id: 'schemas.response.responseDateFrom' })}
                                fieldLabelTo={formatMessage({ id: 'schemas.response.responseDateTo' })}
                              />
                            </FilterSection>
                            <SpendFilter spendFilterNamePrefix="request.subscriptions.target" defaultCollapsed />
                            {canAccessAssessmentTemplate && (
                              <AssessmentTemplateSelectorWithLevelFilter
                                fetchKey={FetchKey.Response}
                                facetEndpoint={endpoints.responsesWithFacets}
                                allowedFilters={allowedFilters}
                                assessmentFilterNamePrefix="request.subscriptions.target.assessments"
                              />
                            )}
                            <FilterSection
                              defaultCollapsed
                              label={formatMessage({ id: 'schemas.filter.additionalInformation' })}
                            >
                              <Filter operator={Operators.In} name="request.subscriptions.target.providerOwners.userId">
                                {({ value, onChange, filterName }) => (
                                  <FilterFacetSelect
                                    implicitFilters={implicitFilters}
                                    size="small"
                                    multiple
                                    facetsParam={{
                                      key: [FetchKey.ResponseFacets, filterName],
                                      endpoint: endpoints.responsesWithFacets,
                                      facetsParam: [{ name: 'request.subscriptions.target.providerOwners.user.name' }],
                                      sort: { target: 'label', order: SortOrder.ASCENDING },
                                    }}
                                    filterName={filterName}
                                    onChange={onChange}
                                    fieldLabel={formatMessage({ id: 'schemas.provider.ownerUserIds' })}
                                    value={[value].flat()}
                                  />
                                )}
                              </Filter>

                              <Filter operator={Operators.In} name="request.subscriptions.target.finalRiskRating">
                                {({ value, onChange, filterName }) => (
                                  <FilterFacetSelect
                                    implicitFilters={implicitFilters}
                                    size="small"
                                    multiple
                                    facetsParam={{
                                      key: [FetchKey.ResponseFacets, filterName],
                                      endpoint: endpoints.responsesWithFacets,
                                      facetsParam: [
                                        { name: 'request.subscriptions.target.finalRiskRating', isEnum: true },
                                      ],
                                    }}
                                    renderOption={(props, option, { selected }): JSX.Element => (
                                      <li {...props}>
                                        <OptionIcon selected={selected} multiple />
                                        <FinalRiskRatingIcon value={option?.label} />
                                        {option?.label
                                          ? formatMessage({
                                              id: `schemas.provider.finalRiskRatingValues.${option.label}`,
                                            })
                                          : ''}
                                      </li>
                                    )}
                                    renderTags={(value, getTagProps) =>
                                      value?.map(
                                        (option, index) =>
                                          option && (
                                            <Chip
                                              {...getTagProps({ index })}
                                              key={option?.value?.toString() || index}
                                              icon={<FinalRiskRatingIcon value={option.label} />}
                                              label={formatMessage({
                                                id: `schemas.provider.finalRiskRatingValues.${option.label}`,
                                              })}
                                              size="small"
                                            />
                                          ),
                                      )
                                    }
                                    filterName={filterName}
                                    onChange={onChange}
                                    fieldLabel={formatMessage({ id: 'schemas.provider.finalRiskRating' })}
                                    value={[value].flat()}
                                  />
                                )}
                              </Filter>

                              <Filter operator={Operators.In} name="request.subscriptions.target.priority">
                                {({ value, onChange, filterName }) => (
                                  <FilterFacetSelect
                                    implicitFilters={implicitFilters}
                                    size="small"
                                    multiple
                                    facetsParam={{
                                      key: [FetchKey.ResponseFacets, filterName],
                                      endpoint: endpoints.responsesWithFacets,
                                      facetsParam: [{ name: 'request.subscriptions.target.priority', isEnum: true }],
                                    }}
                                    renderOption={(props, option, { selected }) => (
                                      <OptionWithLabel
                                        key={option.label}
                                        props={props}
                                        label={formatMessage({
                                          id: option?.label
                                            ? `schemas.provider.priorityValues.${option.label}`
                                            : 'general.notAvailable',
                                        })}
                                        multiple
                                        selected={selected}
                                      />
                                    )}
                                    renderTags={(value, getTagProps) =>
                                      value?.map(
                                        (option, index) =>
                                          option && (
                                            <Chip
                                              {...getTagProps({ index })}
                                              key={option?.value?.toString() || index}
                                              label={formatMessage({
                                                id: `schemas.provider.priorityValues.${option.label}`,
                                              })}
                                              size="small"
                                            />
                                          ),
                                      )
                                    }
                                    filterName={filterName}
                                    onChange={onChange}
                                    fieldLabel={formatMessage({ id: 'schemas.provider.priority' })}
                                    value={[value].flat()}
                                  />
                                )}
                              </Filter>

                              {solution !== Solutions.Finance && (
                                <>
                                  <Filter operator={Operators.In} name="request.subscriptions.target.activityStatus">
                                    {({ value, onChange, filterName }) => (
                                      <FilterFacetSelect
                                        implicitFilters={implicitFilters}
                                        size="small"
                                        multiple
                                        facetsParam={{
                                          key: [FetchKey.ResponseFacets, filterName],
                                          endpoint: endpoints.responsesWithFacets,
                                          facetsParam: [
                                            { name: 'request.subscriptions.target.activityStatus', isEnum: true },
                                          ],
                                        }}
                                        renderOption={(props, option, { selected }) => (
                                          <OptionWithLabel
                                            key={option.label}
                                            props={props}
                                            label={formatMessage({
                                              id: option?.label
                                                ? `schemas.provider.activityStatusValues.${option.label}`
                                                : 'general.notAvailable',
                                            })}
                                            multiple
                                            selected={selected}
                                          />
                                        )}
                                        renderTags={(value, getTagProps) =>
                                          value?.map(
                                            (option, index) =>
                                              option && (
                                                <Chip
                                                  {...getTagProps({ index })}
                                                  key={option?.value?.toString() || index}
                                                  label={formatMessage({
                                                    id: `schemas.provider.activityStatusValues.${option.label}`,
                                                  })}
                                                  size="small"
                                                />
                                              ),
                                          )
                                        }
                                        filterName={filterName}
                                        onChange={onChange}
                                        fieldLabel={formatMessage({ id: 'schemas.provider.activityStatus' })}
                                        value={[value].flat()}
                                      />
                                    )}
                                  </Filter>

                                  <Filter
                                    operator={Operators.In}
                                    name="request.subscriptions.target.providerApprovalStatus"
                                  >
                                    {({ value, onChange, filterName }) => (
                                      <FilterFacetSelect
                                        implicitFilters={implicitFilters}
                                        size="small"
                                        multiple
                                        facetsParam={{
                                          key: [FetchKey.ResponseFacets, filterName],
                                          endpoint: endpoints.responsesWithFacets,
                                          facetsParam: [
                                            {
                                              name: 'request.subscriptions.target.providerApprovalStatus',
                                              isEnum: true,
                                            },
                                          ],
                                        }}
                                        renderOption={(props, option, { selected }) => (
                                          <OptionWithLabel
                                            key={option.label}
                                            props={props}
                                            label={formatMessage({
                                              id: option?.label
                                                ? `schemas.provider.providerApprovalStatusValues.${option.label}`
                                                : 'general.notAvailable',
                                            })}
                                            multiple
                                            selected={selected}
                                          />
                                        )}
                                        renderTags={(value, getTagProps) =>
                                          value?.map(
                                            (option, index) =>
                                              option && (
                                                <Chip
                                                  {...getTagProps({ index })}
                                                  key={option?.value?.toString() || index}
                                                  label={formatMessage({
                                                    id: `schemas.provider.providerApprovalStatusValues.${option.label}`,
                                                  })}
                                                  size="small"
                                                />
                                              ),
                                          )
                                        }
                                        filterName={filterName}
                                        onChange={onChange}
                                        fieldLabel={formatMessage({ id: 'schemas.provider.providerApprovalStatus' })}
                                        value={[value].flat()}
                                      />
                                    )}
                                  </Filter>
                                </>
                              )}
                              {renderWithPermission({
                                [Permissions.SOURCING_USER]: (
                                  <>
                                    <Filter operator={Operators.In} name="request.subscriptions.target.supplierUsage">
                                      {({ value, onChange, filterName }) => (
                                        <FilterFacetSelect
                                          implicitFilters={implicitFilters}
                                          size="small"
                                          multiple
                                          facetsParam={{
                                            key: [FetchKey.ResponseFacets, filterName],
                                            endpoint: endpoints.responsesWithFacets,
                                            facetsParam: [
                                              { name: 'request.subscriptions.target.supplierUsage', isEnum: true },
                                            ],
                                          }}
                                          renderOption={(props, option, { selected }) => (
                                            <OptionWithLabel
                                              key={option.label}
                                              props={props}
                                              label={formatMessage({
                                                id: option?.label
                                                  ? `schemas.provider.supplierUsageValues.${option.label}`
                                                  : 'general.notAvailable',
                                              })}
                                              multiple
                                              selected={selected}
                                            />
                                          )}
                                          renderTags={(value, getTagProps) =>
                                            value?.map(
                                              (option, index) =>
                                                option && (
                                                  <Chip
                                                    {...getTagProps({ index })}
                                                    key={option?.value?.toString() || index}
                                                    label={formatMessage({
                                                      id: `schemas.provider.supplierUsageValues.${option.label}`,
                                                    })}
                                                    size="small"
                                                  />
                                                ),
                                            )
                                          }
                                          filterName={filterName}
                                          onChange={onChange}
                                          fieldLabel={formatMessage({ id: 'schemas.provider.supplierUsage' })}
                                          value={[value].flat()}
                                        />
                                      )}
                                    </Filter>

                                    <Filter operator={Operators.In} name="request.subscriptions.target.tier">
                                      {({ value, onChange, filterName }) => (
                                        <FilterFacetSelect
                                          implicitFilters={implicitFilters}
                                          size="small"
                                          multiple
                                          facetsParam={{
                                            key: [FetchKey.ResponseFacets, filterName],
                                            endpoint: endpoints.responsesWithFacets,
                                            facetsParam: [{ name: 'request.subscriptions.target.tier', isEnum: true }],
                                          }}
                                          filterOptions={(options, state) => [...tierFilter(options, state)]}
                                          renderOption={(props, option, { selected }) => (
                                            <OptionWithLabel
                                              key={option.label}
                                              props={props}
                                              label={formatMessage(
                                                {
                                                  id: `schemas.provider.tierValues`,
                                                },
                                                { tier: option?.label ?? '' },
                                              )}
                                              multiple
                                              selected={selected}
                                            />
                                          )}
                                          renderTags={(value, getTagProps) =>
                                            value?.map(
                                              (option, index) =>
                                                option && (
                                                  <Chip
                                                    {...getTagProps({ index })}
                                                    key={option?.value?.toString() || index}
                                                    label={formatMessage(
                                                      {
                                                        id: `schemas.provider.tierValues`,
                                                      },
                                                      { tier: option.label },
                                                    )}
                                                    size="small"
                                                  />
                                                ),
                                            )
                                          }
                                          filterName={filterName}
                                          onChange={onChange}
                                          fieldLabel={formatMessage({ id: 'schemas.provider.tier' })}
                                          value={[value].flat()}
                                        />
                                      )}
                                    </Filter>
                                  </>
                                ),
                              })}
                            </FilterSection>
                            <CustomCategoriesFilters baseFilterName="request.subscriptions.target" />

                            {productMapping && (
                              <ProductMappingFilters
                                fetchKey={FetchKey.ResponseFacets}
                                endpoint={endpoints.responsesWithFacets}
                                filterNamePrefix="request.subscriptions.target"
                                implicitFilters={implicitFilters}
                              />
                            )}
                          </Box>
                        </DrawerViewFilters>,
                      )
                    }
                  />
                </Box>

                {isFiltered && (
                  <Button variant="text" onClick={clearQueryFilter} sx={{ ml: 1 }}>
                    {formatMessage({ id: 'filters.clearAll' })}
                  </Button>
                )}
              </Grid>
            </>
          )
        }}
      </Filters>
    </Grid>
  )
}

export default ResponsesFilters
